/**
 * The one-time calendar card over the grid (owner, 24 October 2026). Once per ACCOUNT: the server
 * keeps `promptAnswered`, stamped by „Nie teraz", by a successful connect and by „Dodaj do
 * Kalendarza Apple". Since 29 October 2026 it is an either/or: Google, or Apple Calendar on an
 * Apple device (`calendarPromptOptions`). Pure.
 */

import type { CalendarFeedRead } from '~/entities/calendar-feed';
import type { TrainWithMeModulesTrainingDtosResponsesGoogleCalendarConnectionResponse as GoogleConnectionDto } from '~/shared/api/generated/model';

/** Which of the two calendars the card offers; at least one is always `true`. */
export interface CalendarPromptOptions {
  readonly google: boolean;
  readonly apple: boolean;
}

/**
 * What the card offers, or `null` for no card. The SERVER must say the trainer never answered —
 * the answer lives on the Google connection's read, so no read (pending, failed, an older
 * backend's 404) asks nothing, whatever the feed says. Nothing is offered once either calendar is
 * already in use: Google connected (or broken, which is still connected) or the feed switched on.
 * Google stands where the environment offers it; Apple only on an Apple device with the feed
 * configured. Wire values are compared strictly: an answer is not a promise.
 */
export function calendarPromptOptions(
  google: GoogleConnectionDto | undefined,
  feed: CalendarFeedRead | undefined,
  appleDevice: boolean,
): CalendarPromptOptions | null {
  if (google?.promptAnswered !== false || google.connected !== false) return null;
  if (feed?.enabled === true) return null;
  const offered = {
    google: google.available === true,
    apple: appleDevice && feed?.available === true && feed.enabled === false,
  };
  return offered.google || offered.apple ? offered : null;
}

/** `googleCalendarPromptOffer` for the either/or card: decided once, on the grid's hand-over. */
export function calendarPromptOffer(
  current: GoogleCalendarPromptOffer,
  settled: boolean,
  options: CalendarPromptOptions | null,
): GoogleCalendarPromptOffer {
  if (current !== 'undecided' || !settled) return current;
  return options === null ? 'notOffered' : 'offered';
}

/**
 * What an offered card draws now, or `null` once it has gone: answered here (even when the write
 * failed), or the latest reads no longer want it — connected on the phone, the feed switched on.
 */
export function calendarPromptShown(
  offer: GoogleCalendarPromptOffer,
  options: CalendarPromptOptions | null,
  answeredHere: boolean,
): CalendarPromptOptions | null {
  return offer === 'offered' && !answeredHere ? options : null;
}

/**
 * Google is offered here, nothing is connected, and the trainer has never answered. No answer —
 * pending, failed, an older backend's 404 — wants nothing; the fields are compared strictly
 * because an answer is a wire value, not a promise.
 */
export function googleCalendarPromptWanted(answer: GoogleConnectionDto | undefined): boolean {
  return calendarPromptOptions(answer, undefined, false) !== null;
}

/**
 * Decided ONCE, on the frame the grid is handed over: the card arrives with the grid or not at
 * all, so its room is never opened over a grid already laid out.
 */
export type GoogleCalendarPromptOffer = 'undecided' | 'offered' | 'notOffered';

/** @param settled whether the screen's one gate has let go — the grid's hand-over. */
export function googleCalendarPromptOffer(
  current: GoogleCalendarPromptOffer,
  settled: boolean,
  answer: GoogleConnectionDto | undefined,
): GoogleCalendarPromptOffer {
  if (current !== 'undecided' || !settled) return current;
  return googleCalendarPromptWanted(answer) ? 'offered' : 'notOffered';
}

/**
 * An offered card stands while the latest answer still wants it. Answered here, it is gone for
 * the rest of the visit even when the write failed — the next read decides after that.
 */
export function googleCalendarPromptShown(
  offer: GoogleCalendarPromptOffer,
  answer: GoogleConnectionDto | undefined,
  answeredHere: boolean,
): boolean {
  return offer === 'offered' && !answeredHere && googleCalendarPromptWanted(answer);
}
